import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import medicalRecordsService from '../services/medicalRecordsService';

const MedicalRecords = () => {
  const [records, setRecords] = useState([]);
  const [sharedRecords, setSharedRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('my-records');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      loadMedicalRecords();
      loadSharedRecords();
    }
  }, [user]);

  const loadMedicalRecords = async () => {
    setLoading(true);
    const result = await medicalRecordsService.getPatientRecords();
    if (result.success) {
      setRecords(result.data);
    }
    setLoading(false);
  };

  const loadSharedRecords = async () => {
    const result = await medicalRecordsService.getSharedRecords();
    if (result.success) {
      setSharedRecords(result.data);
    }
  };

  const handleUploadRecord = async (recordData) => {
    const result = await medicalRecordsService.uploadMedicalRecord(recordData);
    if (result.success) {
      loadMedicalRecords();
      setShowUploadModal(false);
    }
    return result;
  };

  const handleShareRecord = async (recordId, doctorId, permissions) => {
    const result = await medicalRecordsService.shareMedicalRecord(recordId, doctorId, permissions);
    if (result.success) {
      loadSharedRecords();
      setShowShareModal(false);
    }
    return result;
  };

  const handleDownloadRecord = async (recordId) => {
    const result = await medicalRecordsService.downloadMedicalRecord(recordId);
    if (result.success) {
      // Create download link
      const url = window.URL.createObjectURL(result.data);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `medical-record-${recordId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    }
  };

  const RecordCard = ({ record, showShareButton = true }) => (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{record.title}</h3>
          <p className="text-gray-600 mb-2">{record.description}</p>
          <div className="flex items-center text-sm text-gray-500 space-x-4">
            <span>📅 {new Date(record.created_at).toLocaleDateString()}</span>
            <span>👨‍⚕️ {record.doctor_name || 'Self-uploaded'}</span>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              record.record_type === 'lab_result' ? 'bg-blue-100 text-blue-800' :
              record.record_type === 'prescription' ? 'bg-green-100 text-green-800' :
              record.record_type === 'scan' ? 'bg-purple-100 text-purple-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {record.record_type?.replace('_', ' ').toUpperCase()}
            </span>
          </div>
        </div>
        <div className="flex flex-col space-y-2">
          <button
            onClick={() => handleDownloadRecord(record.id)}
            className="text-blue-600 hover:text-blue-700 text-sm font-medium"
          >
            📥 Download
          </button>
          {showShareButton && (
            <button
              onClick={() => {
                setSelectedRecord(record);
                setShowShareModal(true);
              }}
              className="text-green-600 hover:text-green-700 text-sm font-medium"
            >
              📤 Share
            </button>
          )}
        </div>
      </div>
      
      {record.shared_with && record.shared_with.length > 0 && (
        <div className="border-t border-gray-200 pt-4">
          <p className="text-sm text-gray-600 mb-2">Shared with:</p>
          <div className="flex flex-wrap gap-2">
            {record.shared_with.map((share, index) => (
              <span key={index} className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs">
                Dr. {share.doctor_name}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const UploadModal = () => {
    const [uploadData, setUploadData] = useState({
      title: '',
      description: '',
      record_type: 'document',
      file: null
    });
    const [uploading, setUploading] = useState(false);

    const handleFileUpload = async (e) => {
      e.preventDefault();
      if (!uploadData.file || !uploadData.title) return;

      setUploading(true);
      const reader = new FileReader();
      reader.onload = async (event) => {
        const fileData = {
          ...uploadData,
          file_data: event.target.result,
          file_name: uploadData.file.name,
          file_size: uploadData.file.size
        };
        
        const result = await handleUploadRecord(fileData);
        if (!result.success) {
          alert(result.error);
        }
        setUploading(false);
      };
      reader.readAsDataURL(uploadData.file);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900">Upload Medical Record</h3>
            <button
              onClick={() => setShowUploadModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleFileUpload} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
              <input
                type="text"
                value={uploadData.title}
                onChange={(e) => setUploadData({...uploadData, title: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                value={uploadData.description}
                onChange={(e) => setUploadData({...uploadData, description: e.target.value})}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
              <select
                value={uploadData.record_type}
                onChange={(e) => setUploadData({...uploadData, record_type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="document">General Document</option>
                <option value="lab_result">Lab Result</option>
                <option value="prescription">Prescription</option>
                <option value="scan">Medical Scan</option>
                <option value="report">Medical Report</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">File</label>
              <input
                type="file"
                onChange={(e) => setUploadData({...uploadData, file: e.target.files[0]})}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div className="flex space-x-4 pt-4">
              <button
                type="submit"
                disabled={uploading}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-400"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
              <button
                type="button"
                onClick={() => setShowUploadModal(false)}
                className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const ShareModal = () => {
    const [shareData, setShareData] = useState({
      doctorId: '',
      permissions: {
        view: true,
        download: false,
        edit: false
      }
    });
    const [sharing, setSharing] = useState(false);

    const handleShare = async (e) => {
      e.preventDefault();
      if (!shareData.doctorId || !selectedRecord) return;

      setSharing(true);
      const result = await handleShareRecord(selectedRecord.id, shareData.doctorId, shareData.permissions);
      if (!result.success) {
        alert(result.error);
      }
      setSharing(false);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900">Share Medical Record</h3>
            <button
              onClick={() => setShowShareModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleShare} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Doctor ID or Email
              </label>
              <input
                type="text"
                value={shareData.doctorId}
                onChange={(e) => setShareData({...shareData, doctorId: e.target.value})}
                placeholder="Enter doctor ID or email"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={shareData.permissions.view}
                    onChange={(e) => setShareData({
                      ...shareData,
                      permissions: {...shareData.permissions, view: e.target.checked}
                    })}
                    className="mr-2"
                  />
                  View Record
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={shareData.permissions.download}
                    onChange={(e) => setShareData({
                      ...shareData,
                      permissions: {...shareData.permissions, download: e.target.checked}
                    })}
                    className="mr-2"
                  />
                  Download Record
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={shareData.permissions.edit}
                    onChange={(e) => setShareData({
                      ...shareData,
                      permissions: {...shareData.permissions, edit: e.target.checked}
                    })}
                    className="mr-2"
                  />
                  Edit Record
                </label>
              </div>
            </div>

            <div className="flex space-x-4 pt-4">
              <button
                type="submit"
                disabled={sharing}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-400"
              >
                {sharing ? 'Sharing...' : 'Share'}
              </button>
              <button
                type="button"
                onClick={() => setShowShareModal(false)}
                className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Medical Records</h1>
            <p className="text-gray-600 mt-2">Manage and share your medical records securely</p>
          </div>
          <button
            onClick={() => setShowUploadModal(true)}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            📄 Upload Record
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-8">
          <button
            onClick={() => setActiveTab('my-records')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'my-records'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-blue-600'
            }`}
          >
            My Records ({records.length})
          </button>
          <button
            onClick={() => setActiveTab('shared')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'shared'
                ? 'text-blue-600 border-b-2 border-blue-600'
                : 'text-gray-600 hover:text-blue-600'
            }`}
          >
            Shared Records ({sharedRecords.length})
          </button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading medical records...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {activeTab === 'my-records' ? (
              records.length > 0 ? (
                records.map((record) => (
                  <RecordCard key={record.id} record={record} />
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <div className="text-6xl mb-4">📋</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No Medical Records</h3>
                  <p className="text-gray-600 mb-6">Upload your first medical record to get started</p>
                  <button
                    onClick={() => setShowUploadModal(true)}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Upload Record
                  </button>
                </div>
              )
            ) : (
              sharedRecords.length > 0 ? (
                sharedRecords.map((record) => (
                  <RecordCard key={record.id} record={record} showShareButton={false} />
                ))
              ) : (
                <div className="col-span-full text-center py-12">
                  <div className="text-6xl mb-4">🤝</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No Shared Records</h3>
                  <p className="text-gray-600">Records shared with doctors will appear here</p>
                </div>
              )
            )}
          </div>
        )}

        {/* Modals */}
        {showUploadModal && <UploadModal />}
        {showShareModal && <ShareModal />}
      </div>
    </div>
  );
};

export default MedicalRecords;